/**
 * Coach slug registry — Firestore collection `coachSlugs`.
 *
 * Each document: coachSlugs/{slug} → { userId, createdAt }
 *
 * Document ID = slug, so:
 *   - O(1) lookup by slug (no index needed)
 *   - Uniqueness enforced at the Firestore level (a slug doc can only exist once)
 *
 * The slug is also stored on users/{userId}.coachProfile.slug for forward
 * lookup (profile page canonical URL) and for the Discover page link.
 */

import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';

export interface CoachSlugRecord {
  userId: string;
  createdAt: unknown; // Firestore Timestamp
}

const SLUG_COLLECTION = 'coachSlugs';

/** Fetch the userId that owns a given slug, or null if unregistered. */
export async function resolveSlugToUserId(slug: string): Promise<string | null> {
  const ref = doc(db, SLUG_COLLECTION, slug);
  const snap = await getDoc(ref);
  return snap.exists() ? (snap.data() as CoachSlugRecord).userId : null;
}

/**
 * Claims a coach's slug on the server (claimCoachSlug), which writes both
 * coachSlugs/{slug} and coachProfile.slug: the rules let no client write
 * either. A coach claims their own; a signed-in viewer may ask for a
 * verified coach who has none. Returns the slug, or null.
 */
export async function claimCoachSlug(coachId: string): Promise<string | null> {
  const res = await httpsCallable<{ coachId: string }, { slug: string | null }>(
    functions, 'claimCoachSlug',
  )({ coachId });
  return res.data.slug;
}
